import { Link, createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { AngaMark } from "@/components/brand/AngaMark";
import { Button } from "@/components/ui/button";
import { ApiError, authApi, persistSession } from "@/lib/api";
import { usePlatformConfig } from "@/lib/config";

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>) => {
    const err = search["error"];
    return typeof err === "string" ? { error: err } : {};
  },
  head: () => ({
    meta: [
      { title: "Resident login" },
      { name: "description", content: "Sign in to the resident flood risk dashboard." },
    ],
  }),
  component: ResidentLoginPage,
});

/**
 * Normalise a raw Kenyan phone number to E.164 (+254…).
 * Strips spaces, dashes, and parentheses, then fixes the prefix.
 */
function normalisePhone(raw: string): string {
  // Strip whitespace, dashes, parentheses
  let s = raw.replace(/[\s\-()]/g, "");
  // 07… or 01… → +254…
  if (/^0[17]/.test(s)) {
    s = "+254" + s.slice(1);
  } else if (/^254/.test(s)) {
    // 254… → +254…
    s = "+" + s;
  }
  // Already starts with + — leave it
  return s;
}

const KENYAN_PHONE_RE = /^\+254[17]\d{8}$/;

function ResidentLoginPage() {
  const { t } = useTranslation();
  const { platformName, googleEnabled } = usePlatformConfig();
  const search = Route.useSearch();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(search.error ?? null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const id = identifier.trim();
    const isEmail = id.includes("@");

    // Client-side validation for phone numbers
    if (!isEmail) {
      const normalised = normalisePhone(id);
      if (!KENYAN_PHONE_RE.test(normalised)) {
        setError(
          "Enter a valid email address or Kenyan phone number (07… or 01…)",
        );
        return;
      }
    }

    setLoading(true);
    try {
      const result = isEmail
        ? await authApi.userLogin({ email: id, password })
        : await authApi.userLogin({ phone: normalisePhone(id), password });
      persistSession("user", {
        accessToken: result.accessToken,
        refreshToken: result.refreshToken ?? null,
      });
      window.location.assign(result.dashboard || "/app/");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Could not reach the server. Check your connection and try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--primary-soft),_transparent_55%)]" />
      <div className="relative w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-sm md:p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#064E3B] text-white">
            <AngaMark className="h-6 w-6" title={platformName} />
          </div>
          <div>
            <p className="font-display text-lg font-bold leading-none">{platformName}</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              {t("auth.residentLogin")}
            </p>
          </div>
        </div>

        {googleEnabled ? (
          <>
            <Button
              type="button"
              variant="outline"
              className="w-full bg-white text-foreground hover:bg-white"
              onClick={() => {
                window.location.href = authApi.googleStartUrl("user");
              }}
            >
              <GoogleMark />
              {t("common.googleContinue")}
            </Button>
            <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              Or sign in with email or phone
              <span className="h-px flex-1 bg-border" />
            </div>
          </>
        ) : null}

        <form className="space-y-4" onSubmit={onSubmit}>
          <label className="block text-xs font-semibold">
            Email or phone number
            <input
              type="text"
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              autoComplete="username"
              placeholder="you@example.com or 0712 345 678"
            />
          </label>
          <label className="block text-xs font-semibold">
            {t("common.password")}
            <div className="relative mt-2">
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 pr-16 text-sm"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 px-3 text-xs font-semibold text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </label>

          {error && (
            <p className="rounded-md border border-destructive/25 bg-severe-soft px-3 py-2 text-xs text-severe">
              {error}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? t("common.loading") : t("common.login")}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/forgot-password" className="text-primary hover:underline">
            {t("auth.forgotPassword")}
          </Link>
          {" · "}
          <Link to="/signup" className="text-primary hover:underline">
            {t("common.signup")}
          </Link>
          {" · "}
          <Link to="/" className="text-primary hover:underline">
            {t("common.portal")}
          </Link>
        </p>
      </div>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden>
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-3.5 5.7-6.6 7.1l.1.1 6.3 5.3C36.9 41.6 44 36 44 24c0-1.2-.1-2.3-.4-3.5z"
      />
    </svg>
  );
}
